import { useState, useEffect } from "react";
import { MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { prefetchSiteRoute } from "@/lib/sitePrefetch";
import { supabase } from "@/integrations/supabase/client";
import logoMiahma from "@/assets/logo-miahma.png";

interface ConstructionGateProps {
  children: React.ReactNode;
}

const WHATSAPP_URL = "https://wa.me/5516994029363";

const ConstructionGate = ({ children }: ConstructionGateProps) => {
  // Sessão já liberada pela senha nesta aba: mostra o site sem esperar a checagem
  // (o resultado seria o mesmo). Sem liberação, continua esperando o servidor.
  const [isUnlocked, setIsUnlocked] = useState(() => {
    try { return sessionStorage.getItem("construction_unlocked") === "true"; } catch { return false; }
  });
  const [isLoading, setIsLoading] = useState(!isUnlocked);
  const [constructionEnabled, setConstructionEnabled] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState(false);

  useEffect(() => {
    let mounted = true;
    // Só o código da página atual (sem dados) baixa em paralelo à checagem.
    prefetchSiteRoute(window.location.pathname);

    const loadConstructionStatus = async () => {
      const { data, error } = await (supabase as any).rpc("get_construction_status");
      if (mounted) {
        setConstructionEnabled(Boolean(data?.enabled) && !error);
        setIsLoading(false);
      }
    };

    loadConstructionStatus();

    return () => {
      mounted = false;
    };
  }, []);

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data, error } = await (supabase as any).rpc("verify_construction_password", {
      input_password: passwordInput,
    });

    if (!error && data === true) {
      setIsUnlocked(true);
      sessionStorage.setItem("construction_unlocked", "true");
    } else {
      setPasswordError(true);
      setTimeout(() => setPasswordError(false), 2000);
    }
  };

  if (isLoading) return <div className="min-h-screen bg-background" />;
  // Allow public tracking and ouvidoria pages even under construction
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isTrackingRoute = path.startsWith("/rastreio");
  const isOuvidoriaRoute = path.startsWith("/ouvidoria") || host.startsWith("ouvidoria.");
  const isTelaPromoRoute = path.startsWith("/tela-promo") || host.startsWith("telapromo.");
  // Link de redefinição de senha do e-mail: precisa abrir direto, sem a tela de construção
  const isResetPasswordRoute = path.startsWith("/reset-password");
  // Link de pagamento enviado pela equipe (segredo próprio do pedido)
  const isPaymentLinkRoute = path.startsWith("/pagar/");
  // Acompanhamento de compra já feita (pedido protegido por conta ou link seguro do e-mail)
  const isOrderAccessRoute = path.startsWith("/pedido/") || path === "/acompanhar";
  // Ambiente de edição/preview (Lovable, sandbox local): nunca mostra "em construção".
  // Clientes no domínio real (miahma.com.br) continuam vendo normalmente.
  const isPreviewEnv =
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".lovableproject.com") ||
    host.includes("-preview--") ||
    host.includes(".sandbox.");
  if (
    !constructionEnabled ||
    isUnlocked ||
    isPreviewEnv ||
    isTrackingRoute ||
    isOuvidoriaRoute ||
    isTelaPromoRoute ||
    isResetPasswordRoute ||
    isPaymentLinkRoute ||
    isOrderAccessRoute
  )
    return <>{children}</>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted flex flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-md flex flex-col items-center text-center space-y-8">
        <img src={logoMiahma} alt="Miahma" className="h-14 object-contain" />

        <div className="space-y-3">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
            Site em construção
          </h1>
          <p className="text-muted-foreground text-base leading-relaxed">
            Nosso site novo está chegando! Enquanto isso, você pode:
          </p>
        </div>

        <div className="flex flex-col w-full gap-3">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 w-full px-8 py-4 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-medium shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 transition-all hover:-translate-y-0.5"
          >
            <MessageCircle size={20} />
            Comprar pelo WhatsApp
          </a>
        </div>

        {/* Discreet password access */}
        <div className="pt-12 w-full">
          {!showPasswordInput ? (
            <button
              onClick={() => setShowPasswordInput(true)}
              className="text-[10px] text-muted-foreground/30 hover:text-muted-foreground/70 transition-colors"
              aria-label="Acesso restrito"
            >
              ·
            </button>
          ) : (
            <form onSubmit={handleUnlock} className="flex gap-2 max-w-xs mx-auto animate-fade-in">
              <Input
                type="password"
                placeholder="senha"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                className={`h-8 text-xs ${passwordError ? "border-destructive" : ""}`}
                autoFocus
                onBlur={() => !passwordInput && setShowPasswordInput(false)}
              />
              <button
                type="submit"
                className="text-xs text-muted-foreground hover:text-foreground px-2"
              >
                ok
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConstructionGate;
